import React from "react";
import { toast } from "sonner";
import { ArrowRight, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";
import { FACTOR_LABELS, RISK_STYLES, scoreTone } from "@/lib/vendors";
import { Panel } from "@/components/rights/RightsBadges";
import { RiskBadge, ScoreDial } from "@/components/vendors/VendorBadges";
import { Field, NONE, Pick } from "@/components/vendors/NewVendorDialog";

const riskTone = { High: "text-red-600", Medium: "text-amber-600", Low: "text-emerald-600" };

const FactorBar = ({ k, score, weight }) => (
  <div data-testid={`risk-factor-${k}`}>
    <div className="flex justify-between text-[11px]">
      <span className="text-slate-700 font-medium">{FACTOR_LABELS[k]} <span className="text-slate-400 font-normal">· weight {Math.round(weight * 100)}%</span></span>
      <span className="font-mono text-[#003366]" data-testid={`risk-factor-${k}-value`}>{score}/5</span>
    </div>
    <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
      <div className={`h-full rounded-full transition-[width] duration-700 ${score >= 4 ? "bg-red-500" : score >= 3 ? "bg-amber-400" : "bg-emerald-500"}`}
           style={{ width: `${(score / 5) * 100}%` }} />
    </div>
  </div>
);

const Inputs = ({ v, meta, canEdit, onChanged }) => {
  const patch = async (k, val) => {
    try {
      await api.patch(`/vendors/${v.id}`, { [k]: val === NONE ? null : val });
      toast.success("Risk inputs updated, risk recalculated");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Update failed")); }
  };
  const inp = v.risk.inputs;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100" data-testid="risk-inputs">
      <Field label="Access level"><Pick value={v.access_level} onChange={(x) => patch("access_level", x)} disabled={!canEdit}
             options={Object.entries(meta.access_levels)} testId="risk-input-access" /></Field>
      <Field label="Business criticality"><Pick value={v.business_criticality} onChange={(x) => patch("business_criticality", x)} disabled={!canEdit}
             options={Object.entries(meta.criticality)} testId="risk-input-criticality" /></Field>
      <Field label="Processing position" hint={`Effective: ${meta.positions[inp.processing_position]}`}>
        <Pick value={v.processing_position} onChange={(x) => patch("processing_position", x)} none="Derive from RoPA" disabled={!canEdit}
              options={Object.entries(meta.positions)} testId="risk-input-position" /></Field>
      <Field label="Record volume" hint={`Effective: ${inp.volume_band}`}>
        <Pick value={v.record_volume_band} onChange={(x) => patch("record_volume_band", x)} none="Derive from RoPA" disabled={!canEdit}
              options={meta.volume_bands.map((b) => [b, b])} testId="risk-input-volume" /></Field>
      <Field label="Hosting" hint={`Effective: ${meta.hosting[inp.hosting]}`}>
        <Pick value={v.hosting} onChange={(x) => patch("hosting", x)} none="Derive from RoPA assets" disabled={!canEdit}
              options={Object.entries(meta.hosting)} testId="risk-input-hosting" /></Field>
      <Field label="Data sensitivity" hint="Highest sensitivity of the linked RoPA data classes">
        <div className="h-9 flex items-center text-sm text-[#003366] font-medium" data-testid="risk-input-sensitivity">{inp.sensitivity_source}</div>
      </Field>
    </div>
  );
};

export const RiskPanel = ({ v, meta, canEdit, onChanged }) => {
  const r = v.risk;
  const w = meta.risk_model.weights;
  return (
    <Panel eyebrow="Inherent · assessment · residual" title="Risk model" testId="risk-panel"
           actions={<span className="text-[11px] text-slate-500">Reassess every {meta.risk_model.reassess_months[r.final_risk_rating]} months · next due{" "}
             <b className={`font-mono ${v.assessment_overdue ? "text-red-600" : "text-[#003366]"}`} data-testid="risk-next-due">{fmtD(v.next_assessment_due)}</b></span>}>
      <div className="flex items-stretch gap-4 flex-wrap">
        <ScoreDial label="Inherent risk" value={r.inherent_risk_score} tone={riskTone[r.inherent_risk_rating]} testId="risk-inherent"
                   sub={<RiskBadge level={r.inherent_risk_rating} testId="risk-inherent-rating" />} />
        <ArrowRight className="w-4 h-4 text-slate-300 self-center hidden md:block" />
        <ScoreDial label="Assessment score" value={r.assessment_score == null ? null : `${r.assessment_score}%`} tone={scoreTone(r.assessment_score)}
                   sub={r.assessment_score == null ? "No approved assessment yet" : "Latest approved assessment"} testId="risk-assessment" />
        <ArrowRight className="w-4 h-4 text-slate-300 self-center hidden md:block" />
        <ScoreDial label="Residual risk" value={r.residual_risk_score} tone={riskTone[r.final_risk_rating]} testId="risk-residual"
                   sub={`inherent × (1 − ${meta.risk_model.control_effect} × score)`} />
        <div className={`rounded-xl border px-5 py-3 flex flex-col justify-center min-w-[150px] ${RISK_STYLES[r.final_risk_rating]}`} data-testid="risk-final">
          <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Final risk rating</div>
          <div className="text-2xl font-bold" data-testid="risk-final-value">{r.final_risk_rating}</div>
        </div>
      </div>
      {r.automatic_high_risk && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3" data-testid="risk-auto-high">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700"><Zap className="w-3.5 h-3.5" /> Automatic high risk</div>
          <ul className="mt-1 text-xs text-red-700/90 space-y-0.5">{r.automatic_high_risk_reasons.map((x) => <li key={x}>• {x}</li>)}</ul>
          <div className="text-[11px] text-red-700/70 mt-1">Inherent risk is forced to High. A strong assessment cannot bring the final rating below Medium.</div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-5">
        {Object.keys(w).map((k) => <FactorBar key={k} k={k} score={r[k]} weight={w[k]} />)}
        <div className="text-[11px] text-slate-500 self-end">
          Thresholds: High ≥ {meta.risk_model.thresholds.High}, Medium ≥ {meta.risk_model.thresholds.Medium}, otherwise Low (0–100 scale).
        </div>
      </div>
      <Inputs v={v} meta={meta} canEdit={canEdit} onChanged={onChanged} />
    </Panel>
  );
};
